import { type PropsWithChildren, memo, type RefObject } from "react";

import { Video } from "@shopify/hydrogen-react";

import { Heading } from "@components/Heading";
import { Icon } from "@components/Icon";
import { Image } from "@components/Image";
import { Text } from "@components/Text";
import { baseStyles } from "@react/sections/BentoBox/BentoBoxCard/BentoBoxCard.styles";
import {
  type BentoBoxCardAlignX,
  type BentoBoxCardAlignY,
  type BentoBoxCardBackgroundColor,
  type BentoBoxCardContentWidthPercentage,
  type BentoBoxCardTextColor,
  type BentoBoxCardTitleSize,
} from "@root/tablescape/state/bentoBox";
import { tw, type TTailwindString } from "classnames";

const styles = {
  base: ({
    alignX,
    alignY,
    backgroundColor,
    className,
  }: {
    alignX: BentoBoxCardAlignX;
    alignY: BentoBoxCardAlignY;
    backgroundColor: BentoBoxCardBackgroundColor;
    className?: TTailwindString;
  }) =>
    tw(baseStyles.base({ alignX, alignY, className }), {
      [tw("bg-brick-900")]: backgroundColor === "brick",
      [tw("bg-interactive-char-pressed")]: backgroundColor === "char",
      [tw("bg-sage-900")]: backgroundColor === "sage",
      [tw("bg-blue-700")]: backgroundColor === "blue",
      [tw("bg-bone-300")]: backgroundColor === "bone",
    }),
  background: tw(
    "absolute",
    "w-full",
    "h-full",
    "top-0",
    "left-0",
    "object-cover"
  ),
  content: ({
    alignX,
    contentWidthPercentage,
    textColor,
  }: {
    alignX: BentoBoxCardAlignX;
    contentWidthPercentage: BentoBoxCardContentWidthPercentage;
    textColor: BentoBoxCardTextColor;
  }) =>
    tw(baseStyles.content({ alignX, contentWidthPercentage }), {
      [tw(`text-char-50`)]: textColor === "white",
      [tw(`text-primary`)]: textColor === "char",
    }),
  eyebrow: tw("mb-2"),
  description: tw("whitespace-pre-line"),
  videoIcon: ({ textColor }: { textColor: BentoBoxCardTextColor }) =>
    tw(
      {
        [tw(`text-char-50`)]: textColor === "white",
        [tw(`text-primary`)]: textColor === "char",
      },
      "w-10",
      "h-10",
      "lg:w-12",
      "lg:h-12"
    ),
  videoButton: tw(
    "block",
    "border-0",
    "outline-none",
    "bg-transparent",
    "cursor-pointer",
    "p-0"
  ),
  buttons: ({ alignX }: { alignX: BentoBoxCardAlignX }) =>
    tw(
      {
        [tw("relative")]: alignX === "left",
        [tw(
          "absolute",
          "top-0",
          "bottom-0",
          "right-4",
          "py-4",
          "md:right-6",
          "md:py-6"
        )]: alignX === "center",
      },
      "ml-auto",
      "flex",
      "flex-col",
      "justify-between",
      "items-end",
      "h-full"
    ),
  arrowButton: tw(
    "block",
    "self-end",
    "p-3",
    "lg:p-4.5",
    "rounded-100",
    "border",
    "border-solid",
    "border-primary",
    "outline-none",
    "bg-char-50",
    "hover:bg-char-100",
    "active:bg-char-200",
    "cursor-pointer",
    "transition-colors",
    "duration-400",
    "ease-out",
    "mt-auto"
  ),
  arrowIcon: tw(
    "w-4",
    "h-4",
    "lg:w-5",
    "lg:h-5",
    "text-primary",
    "flex",
    "items-center"
  ),
};

type Props = {
  alignX?: BentoBoxCardAlignX;
  alignY?: BentoBoxCardAlignY;
  backgroundColor?: BentoBoxCardBackgroundColor;
  btnLabel?: string;
  className?: TTailwindString;
  contentWidthPercentage: BentoBoxCardContentWidthPercentage;
  descriptionText?: string;
  eyebrowText?: string;
  handleCtaClick?: () => void;
  handleToggleVideo: () => void;
  imageUrl?: string;
  isVideoPlaying: boolean;
  loopVideo: boolean;
  position: number;
  textColor?: BentoBoxCardTextColor;
  titleSize?: BentoBoxCardTitleSize;
  titleText?: string;
  videoRef: RefObject<HTMLVideoElement>;
  videoSources?: {
    mimeType?: string;
    url?: string;
  }[];
};

const BentoBoxCardViewBase = ({
  alignX = "left",
  alignY = "bottom",
  backgroundColor = "bone",
  btnLabel,
  className,
  contentWidthPercentage,
  descriptionText,
  eyebrowText,
  handleCtaClick,
  handleToggleVideo,
  imageUrl,
  isVideoPlaying,
  loopVideo,
  position,
  textColor = "char",
  titleSize = "lg",
  titleText,
  videoRef,
  videoSources,
}: PropsWithChildren<Props>) => (
  <div className={styles.base({ alignX, alignY, backgroundColor, className })}>
    {!videoSources?.length && imageUrl && (
      <Image className={styles.background} src={imageUrl} width={352} />
    )}
    {!!videoSources?.length && (
      <Video
        autoPlay
        className={styles.background}
        controls={false}
        data={{
          mediaContentType: "VIDEO",
          sources: videoSources,
        }}
        {...(loopVideo && { loop: true })}
        data-chromatic="ignore"
        muted
        ref={videoRef}
      />
    )}
    <div
      className={styles.content({
        alignX,
        contentWidthPercentage,
        textColor,
      })}
    >
      {eyebrowText && (
        <Text as="body2" className={styles.eyebrow}>
          {eyebrowText}
        </Text>
      )}
      {titleText && (
        <Heading as="h3" size={titleSize}>
          {titleText}
        </Heading>
      )}
      {descriptionText && (
        <Text as="body1" className={styles.description}>
          {descriptionText}
        </Text>
      )}
    </div>
    {(!!videoSources?.length || handleCtaClick) && (
      <div className={styles.buttons({ alignX })}>
        {!!videoSources?.length && (
          <button
            className={styles.videoButton}
            data-mtr-as="clickModule"
            data-mtr-position={position}
            data-mtr-track="click"
            onClick={handleToggleVideo}
          >
            <Icon
              className={styles.videoIcon({ textColor })}
              name={isVideoPlaying ? "pause" : "play-circle"}
            />
            <span className={tw("sr-only")}>
              {isVideoPlaying ? "pause" : "play"}
            </span>
          </button>
        )}
        {handleCtaClick && (
          <button
            className={styles.arrowButton}
            data-mtr-as="clickModule"
            data-mtr-label={titleText}
            data-mtr-position={position}
            data-mtr-track="click"
            onClick={handleCtaClick}
          >
            <Icon className={styles.arrowIcon} name="arrow" />
            <span className={tw("sr-only")}>{btnLabel}</span>
          </button>
        )}
      </div>
    )}
  </div>
);

export const BentoBoxCardView = memo(BentoBoxCardViewBase);
